import type { ReactNode } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Popover from '@mui/material/Popover'
import Typography from '@mui/material/Typography'
import { RiNotification3Line } from '@/shared/icons/remix'
import {
  useBellPanelNotifications,
  useBellUnreadCount,
} from '../api/notifications-queries'
import { runDetached } from '@/shared/lib/runDetachedPromise'
import {
  formatPanelTitle,
  NOTIFICATIONS_PANEL_EMPTY,
  NOTIFICATIONS_PANEL_LOAD_ERROR,
  NOTIFICATIONS_RETRY,
  NOTIFICATIONS_VIEW_ALL,
} from '../lib/notifications-copy'
import { useNotificationSocketStore } from '../lib/notificationSocketStore'
import {
  notificationPanelFooterSx,
  notificationPanelHeaderSx,
  notificationPanelListSx,
  notificationPanelPaperSx,
  notificationPanelTitleSx,
  notificationPanelViewAllSx,
} from '../styles/notificationPanelStyles'
import { NotificationItem } from './NotificationItem'
import { NotificationConnectionLostBanner } from './NotificationConnectionLostBanner'
import { NotificationPanelSkeleton } from './NotificationPanelSkeleton'

type NotificationPanelProps = {
  anchorEl: HTMLElement | null
  open: boolean
  onClose: () => void
  onViewAll: () => void
  onNavigate: (url: string) => void
}

export function NotificationPanel({
  anchorEl,
  open,
  onClose,
  onViewAll,
  onNavigate,
}: Readonly<NotificationPanelProps>) {
  const connectionLost = useNotificationSocketStore((s) => s.connectionLost)
  const { data: unreadCount = 0 } = useBellUnreadCount()
  const { data: notifications = [], isPending, isError, refetch } = useBellPanelNotifications()

  const handleNavigate = (url: string) => {
    onNavigate(url)
    onClose()
  }

  let listBody: ReactNode
  if (isPending) {
    listBody = <NotificationPanelSkeleton />
  } else if (isError) {
    listBody = (
      <Box
        data-testid="provider-notification-panel-error"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1,
          px: 3,
          py: 4,
        }}
      >
        <Typography sx={{ fontSize: 14, color: '#6B7280', textAlign: 'center' }}>
          {NOTIFICATIONS_PANEL_LOAD_ERROR}
        </Typography>
        <Button
          size="small"
          onClick={() => runDetached(refetch())}
          data-testid="provider-notification-retry"
        >
          {NOTIFICATIONS_RETRY}
        </Button>
      </Box>
    )
  } else if (notifications.length === 0) {
    listBody = connectionLost ? null : (
      <Box
        data-testid="provider-notification-empty"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1,
          px: 3,
          py: 5,
        }}
      >
        <RiNotification3Line size={32} color="#D1D5DB" aria-hidden />
        <Typography sx={{ fontSize: 14, fontWeight: 500, color: '#6B7280' }}>
          {NOTIFICATIONS_PANEL_EMPTY}
        </Typography>
      </Box>
    )
  } else {
    listBody = notifications.map((notification) => (
      <NotificationItem
        key={notification.id}
        notification={notification}
        onNavigate={handleNavigate}
        compact
      />
    ))
  }

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      data-testid="provider-notification-panel"
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      slotProps={{
        paper: { sx: notificationPanelPaperSx },
      }}
    >
      <Box sx={notificationPanelHeaderSx}>
        <Typography sx={notificationPanelTitleSx} data-testid="provider-notification-panel-title">
          {formatPanelTitle(unreadCount)}
        </Typography>
      </Box>

      <Box sx={notificationPanelListSx} data-testid="provider-notification-list">
        <NotificationConnectionLostBanner
          testId="provider-notification-panel-connection-lost"
          variant="inset"
        />
        {listBody}
      </Box>

      <Box sx={notificationPanelFooterSx}>
        <Button
          onClick={onViewAll}
          data-testid="provider-notification-view-all"
          sx={notificationPanelViewAllSx}
        >
          {NOTIFICATIONS_VIEW_ALL}
        </Button>
      </Box>
    </Popover>
  )
}
